import React, { useState } from 'react';
import { ColumnsType, EditIcon, Table } from '@/components/_common';
import {
  WorkOrderItem,
  WorkOrderItemType,
  workorderTypeColor,
  workorderTypeItem,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { Form, Space } from 'antd';
import StatusTag from '@/components/_common/StatusTag';
import { useIntl } from 'react-intl';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { quantityFormatter } from '@/utils/number';
import Authorities from '@/types/Authority';
import { ComplementReplicateFormData } from '../ReplicateComplementModal';
import ReplicateComplementUpdateItemModal, {
  ComplementReplicateItemFormData,
} from './ReplicateComplementUpdateItemModal';
import { useDisclosure } from '@/hooks';
import formatterPrice from '@/utils/number/formatterPrice';
import { hasPermissions } from '@/utils/functions/hasPermissions';

interface Props {
  setSelectedKeys: React.Dispatch<React.SetStateAction<React.Key[]>>;
  selectedKeys: React.Key[];
}

const ReplicateComplementItemsList: React.FC<Props> = ({
  setSelectedKeys,
  selectedKeys,
}) => {
  const intl = useIntl();

  const form = Form.useFormInstance<ComplementReplicateFormData>();
  const items = Form.useWatch('items', form);

  const [modalUpdatedOpened, modalUpdatedAction] = useDisclosure();
  const [complementItemSelected, setComplementItemSelected] =
    useState<WorkOrderItem>();

  const handleSelectItem = (selectedRowKeys: React.Key[]) => {
    if (selectedRowKeys.length) {
      setSelectedKeys(selectedRowKeys);
    } else {
      setSelectedKeys([]);
    }
  };

  const columns: ColumnsType<WorkOrderItem> = [
    useConfigurationColumnsPart({ internalCode: 'sku' }),
    {
      dataIndex: 'type',
      title: intl.formatMessage({
        id: 'workOrders.domain.items.type',
      }),
      width: 150,
      ellipsis: true,
      render: (_, item) =>
        item.type === WorkOrderItemType.Part ||
        item.type === WorkOrderItemType.Service ? (
          <StatusTag
            color={workorderTypeColor[item.type]}
            label={workorderTypeItem[`${item.type}/${item.origin}`]}
          />
        ) : (
          '-'
        ),
    },
    {
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'workOrders.domain.items.description',
      }),
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      dataIndex: 'quantity',
      width: COLUMNS_WIDTH.extraSmall,
      ellipsis: true,
      title: intl.formatMessage({
        id: 'workOrders.domain.items.quantityTime',
      }),
      align: 'right',
      render: (value: number) => (value ? quantityFormatter(value) : '-'),
    },
    {
      dataIndex: 'unitPrice',
      title: intl.formatMessage({
        id: 'workOrders.domain.items.unitPrice',
      }),
      width: COLUMNS_WIDTH.price,
      ellipsis: true,
      align: 'right',
      render: (value: number) => formatterPrice(value),
    },
    {
      dataIndex: 'discount',
      title: intl.formatMessage({
        id: 'workOrders.domain.items.discount',
      }),
      width: COLUMNS_WIDTH.price,
      ellipsis: true,
      align: 'right',
      render: (value: number) => formatterPrice(value * 100),
    },
    {
      title: intl.formatMessage({
        id: 'workOrders.domain.items.discountValue',
      }),
      align: 'right',
      width: COLUMNS_WIDTH.price,
      ellipsis: true,
      render: (_, row) =>
        formatterPrice(row.quantity * row.unitPrice * row.discount),
    },
    {
      title: intl.formatMessage({
        id: 'workOrders.domain.items.totalValue',
      }),
      align: 'right',
      width: COLUMNS_WIDTH.price,
      ellipsis: true,
      render: (_, row) =>
        formatterPrice(
          row.quantity * row.unitPrice -
            row.quantity * row.unitPrice * row.discount,
        ),
    },
    {
      title: intl.formatMessage({
        id: 'commons.list.header.action',
      }),
      width: 50,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <Space>
          <>
            <EditIcon
              disabled={
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_AUTHORIZED_EDIT_ITEM,
                ]) ||
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_OPENED_EDIT_ITEM,
                ]) ||
                !hasPermissions([Authorities.MOVIMENTATION_WORKORDER_EDIT_ITEM])
              }
              iconProps={{
                onClick: () => {
                  modalUpdatedAction.open();
                  setComplementItemSelected(row);
                },
              }}
            />
          </>
        </Space>
      ),
    },
  ];

  const handleCallback = (values: ComplementReplicateItemFormData) => {
    if (values && complementItemSelected && items?.length) {
      const itemFinded = items.find(
        (item) => item.id === complementItemSelected.id,
      );

      if (itemFinded) {
        setSelectedKeys((prev) => [...prev, itemFinded.id]);
      }

      const itemIndex = items.findIndex(
        (item) => item.id === complementItemSelected.id,
      );

      items[itemIndex] = {
        ...complementItemSelected,
        description: values.itemName,
        isInternal: JSON.parse(values.isInternal),
        isPaintedPart: JSON.parse(values.isPaintedPart),
        discount: values.discount / 100,
        unitCost: values.unitCost,
        unitPrice: values.unitPrice,
        quantity: values.quantity,
        subArea: { id: values.subAreaId },
      } as WorkOrderItem;

      form.setFieldValue('items', [...items]);
    }
  };

  return (
    <>
      <Form.List name="items">
        {() => (
          <Table<WorkOrderItem>
            columns={columns}
            dataSource={items}
            loading={false}
            rowKey={(row) => row.id}
            scroll={{ x: getTableScrollX(columns) }}
            rowSelection={{
              preserveSelectedRowKeys: true,
              selectedRowKeys: selectedKeys,
              onChange: handleSelectItem,
            }}
          />
        )}
      </Form.List>

      <ReplicateComplementUpdateItemModal
        onCallback={handleCallback}
        open={modalUpdatedOpened}
        onCancel={modalUpdatedAction.close}
        complementItemSelected={complementItemSelected}
      />
    </>
  );
};

export default ReplicateComplementItemsList;
